<template>
  <v-col cols="12" class="pa-0">
    <v-col cols="12" class="px-0 py-0 mb-5">
      <v-btn color="primary" icon @click="handleBack">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="text-h6 mr-2" style="vertical-align: middle">
        {{ $t('message.pages.video.metricsPage.views') }}
      </span>
    </v-col>
    <v-container class="pa-0 px-2">
      <v-col class="datepicker-container pa-0 mb-4">
        <date-picker
          :shortcuts="shortcuts"
          :clearable="false"
          :disabled-date="verifyDisabledDate"
          @change="fetchData"
          style="width: 100%"
          v-model="dateRange"
          confirm
          range
        >
          <template v-slot:input>
            <v-text-field
              :label="$t('message.date')"
              dense
              :value="dateRangeValue"
              outlined
              prepend-inner-icon="mdi-calendar"
              readonly
              hide-details
            ></v-text-field>
          </template>
          <template v-slot:icon-calendar>
            <v-icon style="display: none">mdi-calendar</v-icon>
          </template>
        </date-picker>
      </v-col>
      <v-card flat v-if="loadings.fetch">
        <loading-logo />
      </v-card>
      <v-card flat v-else-if="errors.fetch">
        <ad-block-error-message
          v-if="isAdBlockEnabled"
          :isLoading="loadings.fetch"
          @handleFetchData="fetchData"
        />
        <v-row justify="center" class="mt-5 mb-8" no-gutters v-else>
          <v-col cols="12" class="py-0 d-flex justify-center">
            <span>
              {{ $t('message.pages.video.metricsPage.error') }}
            </span>
          </v-col>
          <v-col cols="12" class="py-0 d-flex justify-center">
            <v-btn class="mt-3" color="primary" @click="fetchData">{{
              $t('message.pages.video.metricsPage.tryAgain')
            }}</v-btn>
          </v-col>
        </v-row>
      </v-card>
      <v-card flat v-else>
        <v-row>
          <v-col>
            <v-card>
              <v-col class="pa-6">
                <v-row>
                  <span class="text-caption text--secondary">{{
                    $t('message.pages.video.metricsPage.views')
                  }}</span>
                </v-row>
                <v-row>
                  <span class="text-h4 primary--text">{{
                    doThousandsDotIndicator(totalData.views)
                  }}</span>
                </v-row>
              </v-col>
            </v-card>
          </v-col>
          <v-col>
            <v-card>
              <v-col class="pa-6">
                <v-row>
                  <span class="text-caption text--secondary">
                    {{ $t('message.pages.video.metricsPage.uniqueViews') }}
                  </span>
                </v-row>
                <v-row>
                  <span class="text-h4 primary--text">{{
                    doThousandsDotIndicator(totalData.uniqueViews)
                  }}</span>
                </v-row>
              </v-col>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-card>
              <v-col class="pa-6">
                <v-row>
                  <span class="text-caption text--secondary">
                    {{ $t('message.pages.video.metricsPage.plays') }}
                  </span>
                </v-row>
                <v-row>
                  <span class="text-h4 primary--text">{{
                    doThousandsDotIndicator(totalData.plays)
                  }}</span>
                  <p
                    class="d-flex align-end justify-end caption primary--text pb-1 mb-0"
                  >
                    ({{ totalData.playRate }}%)
                  </p>
                </v-row>
              </v-col>
            </v-card>
          </v-col>
          <v-col>
            <v-card>
              <v-col class="pa-6">
                <v-row>
                  <span class="text-caption text--secondary">
                    {{ $t('message.pages.video.metricsPage.uniquePlays') }}
                  </span>
                </v-row>
                <v-row>
                  <span class="text-h4 primary--text">{{
                    doThousandsDotIndicator(totalData.uniquePlays)
                  }}</span>
                  <p
                    class="d-flex align-end justify-end caption primary--text pb-1 mb-0"
                  >
                    ({{ totalData.uniquePlayRate }}%)
                  </p>
                </v-row>
              </v-col>
            </v-card>
          </v-col>
        </v-row>
        <apexchart
          :class="{ darkThemeSvgFix: $vuetify.theme.dark }"
          type="area"
          height="250"
          :options="viewsChartOptions"
          :series="viewChartSeries"
        ></apexchart>
      </v-card>
      <v-card flat class="mt-2 px-0" v-if="!loadings.fetch">
        <v-col cols="12" class="pt-0 px-0">
          <small class="text--secondary">{{
            $t('message.pages.video.metricsPage.updatedAtEachSec', {
              time: updateAnalyticsTime.time,
            })
          }}</small>
        </v-col>
      </v-card>
    </v-container>
  </v-col>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import analyticsApi from '@/services/analyticsApi'
import {
  subHours,
  subDays,
  subMonths,
  isAfter,
  isBefore,
  format,
  differenceInDays,
  addDays,
} from 'date-fns'
import LoadingLogo from '../../../components/LoadingLogo.vue'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/locale/pt-br'
import i18n from '@/translations/lang'
import { mapState, mapMutations } from 'vuex'
import realtimeAnalyticsMixin from '@/utils/realtimeAnalytics.mixin.js'
import AdBlockErrorMessage from '@/components/AdBlockErrorMessage.vue'

export default {
  name: 'VideoViews',
  components: {
    apexchart: VueApexCharts,
    LoadingLogo,
    DatePicker,
    AdBlockErrorMessage,
  },
  props: ['handleBack', 'customVideoId'],
  mixins: [realtimeAnalyticsMixin],
  data: () => ({
    subMenuIndex: 0,
    isBeta: false,
    totalData: {
      views: 0,
      uniqueViews: 0,
      uniquePlays: 0,
      plays: 0,
      playRate: 0,
    },
    dateRange: [],
    loadings: {
      fetch: true,
    },
    errors: {
      fetch: false,
    },
    datePickerOpen: false,
    viewsChartOptions: {
      chart: {
        toolbar: false,
        type: 'area',
        height: 350,
        width: '100%',
        offsetX: 0,
        offsetY: 0,
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        type: 'datetime',
        categories: [],
      },
      tooltip: {
        theme: 'dark',
        x: {
          format: 'dd/MM/yy HH:mm',
        },
        y: {
          formatter: function (value, { seriesIndex, dataPointIndex, series }) {
            if (seriesIndex === 3 && value >= 0) {
              const uniquePlays = value
              const uniqueViews = series[1][dataPointIndex]
              let playRate = 0
              if (uniquePlays && uniqueViews) {
                playRate = ((uniquePlays * 100) / uniqueViews).toFixed(1)
              }
              return `${value} (${playRate}%)`
            } else if (seriesIndex === 2 && value >= 0) {
              const plays = value
              const views = series[0][dataPointIndex]
              let playRate = 0
              if (plays && views) {
                playRate = ((plays * 100) / views).toFixed(1)
              }
              return `${value} (${playRate}%)`
            }
            return value
          },
        },
      },
      stroke: {
        curve: 'smooth',
      },
    },
    viewChartSeries: [],
    shortcuts: [
      {
        text: i18n.t('message.pages.video.metricsPage.datepicker.today'),
        onClick() {
          const date = new Date()
          return [date, date]
        },
      },
      {
        text: i18n.t('message.pages.video.metricsPage.datepicker.yesterday'),
        onClick() {
          const date = new Date()

          return [subDays(date, 1), date]
        },
      },
      {
        text: i18n.t('message.pages.video.metricsPage.datepicker.7days'),
        onClick() {
          const date = new Date()

          return [subDays(date, 7), date]
        },
      },
      {
        text: i18n.t('message.pages.video.metricsPage.datepicker.aMonth'),
        onClick() {
          const date = new Date()

          return [subMonths(date, 1), date]
        },
      },
    ],
  }),
  async created() {
    this.dateRange = [subDays(new Date(), 7), new Date()]
  },
  async mounted() {
    this.$root.$emit('checkAdBlock')
    if (this.$vuetify.theme.dark) {
      this.viewsChartOptions.tooltip.theme = 'dark'
    } else {
      this.viewsChartOptions.tooltip.theme = 'light'
    }
    this.isBeta = location.search.match('beta')
    await this.fetchData()
    this.changeUpdateTime()
  },
  beforeDestroy() {
    this.emptyRequestLoop()
  },
  computed: {
    ...mapState('user', {
      planInfo: 'userPlanInfo',
    }),
    ...mapState({
      isAdBlock: (state) => state.dashboard.isAdBlockEnabled,
    }),
    isAdBlockEnabled() {
      if (this.isAdBlock) {
        return this.isAdBlock
      }
      return false
    },
    createdAt() {
      return new Date(this.$store.state.video.video.created_at)
    },
    videoId() {
      return this.customVideoId
        ? this.customVideoId
        : this.$store.state.video.video.id
    },
    dateRangeValue() {
      return `${format(this.dateRange[0], 'dd/MM/yyyy')} - ${format(
        this.dateRange[1],
        'dd/MM/yyyy'
      )}`
    },
  },
  methods: {
    ...mapMutations('dashboard', ['UPDATE_IS_ADBLOCK_ENABLED']),
    doThousandsDotIndicator(val) {
      return val.toLocaleString().replaceAll(',', '.')
    },
    changeUpdateTime() {
      this.emptyRequestLoop()
      this.updateInterval = setInterval(
        this.getDataV2(),
        1000 * this.updateAnalyticsTime.time
      )
    },
    emptyRequestLoop() {
      window.clearInterval(this.updateInterval)
      this.updateInterval = {}
    },
    handleClickInput() {
      this.datePickerOpen = true
    },
    handleClickDatePickerOutside() {
      if (this.datePickerOpen) this.datePickerOpen = false
    },
    verifyDisabledDate(date) {
      const canUse =
        isAfter(this.createdAt, date) || !isBefore(date, new Date())
      return canUse
    },
    async fetchData() {
      this.datePickerOpen = false
      this.loadings.fetch = true
      await this.getDataV2()
      this.loadings.fetch = false
    },
    async getDataV2() {
      try {
        const response = await analyticsApi.getVideoDataV2(
          this.videoId,
          format(this.dateRange[0], 'yyyy-MM-dd'),
          format(this.dateRange[1], 'yyyy-MM-dd')
        )
        this.UPDATE_IS_ADBLOCK_ENABLED(false)
        const data = response.data
        this.totalData.views = data.totalViews
        this.totalData.uniqueViews = data.totalUniqueViews
        this.totalData.plays = data.totalPlays
        this.totalData.uniquePlays = data.totalUniquePlays

        if (data.totalPlays && data.totalViews) {
          this.totalData.playRate = (
            (data.totalPlays * 100) /
            data.totalViews
          ).toFixed(1)
        } else {
          this.totalData.playRate = 0
        }

        if (data.totalUniquePlays && data.totalUniqueViews) {
          this.totalData.uniquePlayRate = (
            (data.totalUniquePlays * 100) /
            data.totalUniqueViews
          ).toFixed(1)
        } else {
          this.totalData.uniquePlayRate = 0
        }

        if (Object.keys(data.viewsData).length === 0) {
          let daysDiff = differenceInDays(this.dateRange[1], this.dateRange[0])
          let fakeTrafficArray = {}
          for (let i = 0; i <= daysDiff; i++) {
            let dateIndex = format(
              addDays(this.dateRange[0], i),
              'yyyy-MM-dd HH:mm'
            )

            fakeTrafficArray[dateIndex] = {}
            fakeTrafficArray[dateIndex].plays =
              fakeTrafficArray[dateIndex].unique_views =
              fakeTrafficArray[dateIndex].views =
                0
          }
          data.viewsData = fakeTrafficArray
        }
        if (Object.keys(data.viewsData).length === 1) {
          let dateTime = Object.keys(data.viewsData)[0].split(' ')[1]
          let dateHours = Number(dateTime.split(':')[0]) + 1
          if (Number(dateHours) > 23) {
            dateHours = '00'
          } else if (Number(dateHours) < 10) {
            dateHours = `0${dateHours}`
          }
          data.viewsData[`2022-04-07 ${dateHours}:00`] = {
            plays: 0,
            unique_views: 0,
            views: 0,
          }
        }

        this.viewChartSeries = [
          {
            name: i18n.t('message.pages.video.metricsPage.views'),
            data: Object.keys(data.viewsData)
              .map((item) => ({
                x: subHours(
                  new Date(item.replace(/-/gm, '/')),
                  (new Date().getTimezoneOffset() / 60) * 2
                ).toISOString(),
                y: data.viewsData[item].view,
              }))
              .sort(function (a, b) {
                return new Date(b.x) - new Date(a.x)
              }),
          },
          {
            name: i18n.t('message.pages.video.metricsPage.uniqueViews'),
            data: Object.keys(data.viewsData)
              .map((item) => ({
                x: subHours(
                  new Date(item.replace(/-/gm, '/')),
                  (new Date().getTimezoneOffset() / 60) * 2
                ).toISOString(),
                y: data.viewsData[item].unique_view,
              }))
              .sort(function (a, b) {
                return new Date(b.x) - new Date(a.x)
              }),
          },
          {
            name: i18n.t('message.pages.video.metricsPage.plays'),
            data: Object.keys(data.viewsData)
              .map((item) => ({
                x: subHours(
                  new Date(item.replace(/-/gm, '/')),
                  (new Date().getTimezoneOffset() / 60) * 2
                ).toISOString(),
                y: data.viewsData[item].play,
              }))
              .sort(function (a, b) {
                return new Date(b.x) - new Date(a.x)
              }),
          },
          {
            name: i18n.t('message.pages.video.metricsPage.uniquePlays'),
            data: Object.keys(data.viewsData)
              .map((item) => ({
                x: subHours(
                  new Date(item.replace(/-/gm, '/')),
                  (new Date().getTimezoneOffset() / 60) * 2
                ).toISOString(),
                y: data.viewsData[item].unique_play,
              }))
              .sort(function (a, b) {
                return new Date(b.x) - new Date(a.x)
              }),
          },
        ]
        this.errors.fetch = false
      } catch (error) {
        const errObject = { error }
        const { response } = errObject
        if (!response?.status) {
          this.UPDATE_IS_ADBLOCK_ENABLED(true)
        }
        console.error('error', error)
        this.errors.fetch = true
      }
    },
  },
}
</script>
<style lang="scss">
@import '~vue2-datepicker/scss/index.scss';
</style>
<style scoped lang="scss">
.datepicker-container {
  .datepicker-item {
    height: 400px;
    position: absolute;
    top: 80px;
    left: 50px;
    z-index: 2000;
  }
}
.disabled {
  display: none;
}
.date-picker-input {
  width: 100%;
}
</style>
